import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { Image as ImageIcon, Video, Upload as UploadIcon, X, ChevronLeft, AlertTriangle, Send } from 'lucide-react';
import { toast } from 'sonner';
import { uploadsApi, validateFile, isHeavyVideo, MAX_IMAGE_MB, MAX_VIDEO_MB } from '@/lib/uploadsApi';
import { prepareImagePreview, prepareVideoPreview } from '@/utils/mediaPreview';
import { postsApi } from '@/lib/postsApi';
import { useAuth } from '@/contexts/AuthContext';
import { useUploadCenter } from '@/contexts/UploadCenterContext';
import { PreviewMuteToggle } from '@/components/Upload/PreviewMuteToggle';
import { tr } from '@/contexts/LanguageContext';

const QUICK_TAGS = ['fade', 'taper', 'beard', 'lineup', 'design', 'transformation'];
const ACCEPT_MIME = 'image/jpeg,image/png,image/webp,image/gif,image/heic,image/heif,video/mp4,video/quicktime,video/webm';

/**
 * Instagram-style direct publish dialog.
 *  Step 1: pick file
 *  Step 2: preview (square for photos, 9:16 for videos) + caption
 *  Submit: upload with progress → create post → close → caller redirects
 *
 * Caller passes `onPublished({post})` to receive the freshly-published
 * backend post for optimistic feed insertion. Progress is reported via the
 * shared <UploadProgressBar/> at the top of <App/>.
 */
export const UploadContentDialog = ({ open, onOpenChange, onPublished }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { enqueue } = useUploadCenter();
  const fileInputRef = useRef(null);
  // Iter 110 — videoRef for the local preview <video>. Drives the
  // PreviewMuteToggle so the barber can hear the audio before publishing.
  const previewVideoRef = useRef(null);

  const [file, setFile] = useState(null);
  const [previewUrl, setPreviewUrl] = useState(null);
  const [kind, setKind] = useState(null);    // 'image' | 'video'  (the source file type)
  // Publish-as picker — for images defaults to 'post'; for videos the user
  // chooses 'reel' (vertical TikTok/IG Reels) or 'post' (square thumbnail
  // that plays fullscreen on tap, lives in the main feed grid).
  const [publishAs, setPublishAs] = useState('post');  // 'post' | 'reel'
  const [caption, setCaption] = useState('');
  const [tags, setTags] = useState([]);
  const [step, setStep] = useState(1);
  const [error, setError] = useState(null);
  const [preparing, setPreparing] = useState(false);

  const reset = () => {
    setFile(null);
    setPreviewUrl(null);
    setKind(null);
    setPublishAs('post');
    setCaption('');
    setTags([]);
    setStep(1);
    setError(null);
  };

  useEffect(() => {
    if (!open) {
      // Defer so the close animation finishes before the form resets visually.
      const t = setTimeout(reset, 250);
      return () => clearTimeout(t);
    }
  }, [open]);

  useEffect(() => () => {
    if (previewUrl) URL.revokeObjectURL(previewUrl);
  }, [previewUrl]);

  const handlePick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = '';
    if (!f) return;
    let detected;
    try {
      detected = validateFile(f, { prefer: 'visual' });
    } catch (err) {
      setError(err.message || tr("No pudimos cargar este archivo"));
      return;
    }
    // Sprint 5 · P0-2 — preview GARANTIZADO visible: valida decodificación
    // real (y convierte HEIC→JPEG) antes de pasar al paso 2. El archivo que
    // se sube es EXACTAMENTE el que se previsualiza.
    setPreparing(true);
    setError(null);
    try {
      const prepared = detected === 'image'
        ? await prepareImagePreview(f)
        : await prepareVideoPreview(f);
      setFile(prepared.file);
      setKind(detected);
      setPublishAs(detected === 'video' ? 'reel' : 'post');
      setPreviewUrl(prepared.url);
      if (detected === 'video' && isHeavyVideo(f)) {
        toast.info(tr("Este video es pesado. La subida puede tardar unos minutos."), {
          duration: 6000,
          id: 'feed-upload-heavy',
        });
      }
      setStep(2);
    } catch (err) {
      setError(err?.message || tr("No pudimos preparar este archivo. Elige otro."));
    } finally {
      setPreparing(false);
    }
  };

  const toggleTag = (t) => {
    setTags((cur) => (cur.includes(t) ? cur.filter((x) => x !== t) : [...cur, t]));
  };

  /**
   * Submit — enqueues the upload into the global UploadCenter, closes the
   * dialog immediately, and routes the user back to the relevant surface.
   * The actual upload + post creation runs in the background so the user
   * can keep scrolling, navigate around, etc. Errors stay in the global
   * banner with a "Reintentar" affordance — no draft is lost.
   */
  const submit = () => {
    if (!file) return;
    if (!user) {
      toast.error(tr("Inicia sesión para publicar."));
      return;
    }
    setError(null);
    const isVideoSource = kind === 'video';
    const chosenKind = isVideoSource
      ? (publishAs === 'post' ? 'video' : 'reel')
      : 'photo';
    // Capture local copies — submit() returns immediately so these survive
    // the reset() that follows.
    const f = file;
    const cap = caption.trim() || null;
    const tagsCopy = [...tags];
    const labelByKind = chosenKind === 'reel' ? tr("Reel") : chosenKind === 'video' ? tr("Post de video") : tr("Foto");
    const itemKind = chosenKind === 'reel' ? 'reel' : 'post';

    enqueue({
      kind: itemKind,
      label: labelByKind,
      draft: { file: f, caption: cap, tags: tagsCopy, publishAs },
      taskFn: async ({ onProgress, onState }) => {
        onState('uploading');
        const uploaded = await uploadsApi.uploadMedia(f, { onProgress });
        onState('processing');
        const payload = {
          kind: chosenKind,
          caption: cap,
          image_url: isVideoSource ? null : uploaded.url,
          video_url: isVideoSource ? uploaded.url : null,
          // Iter 186 — el backend extrae el primer frame del video con
          // ffmpeg y devuelve `thumbnail_url` en la respuesta del upload.
          thumbnail_url: isVideoSource ? (uploaded.thumbnail_url || null) : null,
          tags: tagsCopy,
        };
        const post = await postsApi.create(payload);
        return { post };
      },
      onSuccess: ({ post }) => {
        // Notify feed/reels consumers (same event the legacy code emitted).
        try {
          window.dispatchEvent(new CustomEvent('lineage_posts_updated'));
          window.dispatchEvent(new CustomEvent('lineage_upload_done', {
            detail: { kind: chosenKind, post },
          }));
        } catch { /* noop */ }
        if (typeof onPublished === 'function') {
          onPublished({ post });
        }
      },
    });

    // Close the dialog and route the user back to the Feed — always. The
    // Feed renders photos + post-videos + reels mixed (Mar 1, 2026), so the
    // user sees their freshly-published reel at the top once the upload
    // completes (via the `lineage_posts_updated` listener). We never route
    // the user to the grid /reels surface after publish — that confused
    // beta barbers who expected an Instagram-style vertical feed.
    onOpenChange(false);
    if (typeof onPublished !== 'function') {
      navigate('/feed');
    }
  };

  const showCaptionStep = step === 2 && !!file;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-card border-border max-w-2xl max-h-[92vh] overflow-y-auto p-0" data-testid="upload-dialog">
        <DialogHeader className="px-5 pt-5 pb-3 border-b border-border">
          <div className="flex items-center justify-between">
            {step === 2 ? (
              <Button
                variant="ghost" size="sm" onClick={() => setStep(1)}
                className="-ml-2" data-testid="upload-back"
              >
                <ChevronLeft className="w-4 h-4 mr-1" />{' '}{tr("Atrás")}</Button>
            ) : <span />}
            <DialogTitle className="text-base font-semibold">
              {step === 1 ? tr("Nueva publicación") : tr("Detalles")}
            </DialogTitle>
            <span />
          </div>
          <DialogDescription className="sr-only">{tr("Subí una foto o un video. Se publicará inmediatamente.")}</DialogDescription>
        </DialogHeader>

        {/* Step 1 — file picker */}
        {step === 1 && (
          <div className="p-6">
            <input
              type="file"
              accept={ACCEPT_MIME}
              hidden
              ref={fileInputRef}
              onChange={handlePick}
              data-testid="upload-file-input"
            />
            <button
              onClick={() => fileInputRef.current?.click()}
              disabled={preparing}
              className="w-full aspect-[4/3] sm:aspect-square rounded-2xl border-2 border-dashed border-border hover:border-primary/70 hover:bg-primary/5 transition-all flex flex-col items-center justify-center gap-3 group disabled:opacity-70"
              data-testid="upload-pick-button"
            >
              {preparing ? (
                <div className="flex flex-col items-center gap-3" data-testid="upload-preparing">
                  <div className="w-14 h-14 rounded-full bg-primary/10 flex items-center justify-center">
                    <UploadIcon className="w-7 h-7 text-primary animate-pulse" />
                  </div>
                  <p className="font-semibold text-foreground">{tr("Preparando archivo…")}</p>
                  <p className="text-xs text-muted-foreground">{tr("Validando que se vea perfecto")}</p>
                </div>
              ) : (
              <>
              <div className="w-14 h-14 rounded-full bg-primary/10 group-hover:bg-primary/20 transition-colors flex items-center justify-center">
                <UploadIcon className="w-7 h-7 text-primary" />
              </div>
              <div className="text-center">
                <p className="font-semibold text-foreground">{tr("Selecciona una foto o video")}</p>
                <p className="text-xs text-muted-foreground mt-1">{tr("Foto: hasta {0} MB · Video: hasta {1} MB", MAX_IMAGE_MB, MAX_VIDEO_MB)}</p>
              </div>
              <div className="flex items-center gap-4 text-xs text-muted-foreground mt-2">
                <span className="flex items-center gap-1"><ImageIcon className="w-3.5 h-3.5" /> jpg · png · webp</span>
                <span className="flex items-center gap-1"><Video className="w-3.5 h-3.5" /> mp4 · mov</span>
              </div>
              </>
              )}
            </button>
            {error && (
              <div className="mt-4 flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive" data-testid="upload-error">
                <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <span>{error}</span>
              </div>
            )}
          </div>
        )}

        {/* Step 2 — preview + caption */}
        {showCaptionStep && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
            <div className="bg-black flex items-center justify-center min-h-[280px] relative">
              <button
                onClick={() => { setFile(null); setPreviewUrl(null); setKind(null); setStep(1); }}
                className="absolute top-3 left-3 w-8 h-8 rounded-full bg-black/60 backdrop-blur-md text-white flex items-center justify-center hover:bg-black/80 transition-colors z-10"
                aria-label={tr("Cambiar archivo")}
                data-testid="upload-change-file"
              >
                <X className="w-4 h-4" />
              </button>
              {kind === 'image' ? (
                <div className="aspect-square w-full overflow-hidden" data-testid="upload-preview-image">
                  <img
                    src={previewUrl} alt="preview"
                    className="w-full h-full object-cover"
                    draggable={false}
                    onError={() => {
                      setError(tr("No pudimos mostrar la imagen. Elige otro archivo."));
                      setFile(null); setPreviewUrl(null); setKind(null); setStep(1);
                    }}
                  />
                </div>
              ) : (
                <div
                  className={`${publishAs === 'post' ? 'aspect-square' : 'aspect-[9/16]'} w-full max-h-[60vh] overflow-hidden mx-auto relative`}
                  data-testid="upload-preview-video"
                >
                  <video
                    ref={previewVideoRef}
                    src={previewUrl}
                    className="w-full h-full object-cover"
                    muted autoPlay loop playsInline
                    onError={() => {
                      setError(tr("No pudimos reproducir este video. Usa MP4 (H.264) o elige otro."));
                      setFile(null); setPreviewUrl(null); setKind(null); setStep(1);
                    }}
                  />
                  {/* Iter 110 — IG-parity mute toggle. Local-only; never
                      touches the file we POST to /api/uploads/media. */}
                  <PreviewMuteToggle videoRef={previewVideoRef} testIdSuffix="upload-dialog" />
                </div>
              )}
            </div>

            <div className="p-5 flex flex-col gap-3 pb-24 md:pb-5">
              {/* Publish-as selector — videos can be either a vertical Reel
                  (9:16 in Reels surface) or a post-card video (square
                  thumbnail in Feed, plays fullscreen on tap). For images the
                  selector is hidden because images can only be 'photo'. */}
              {kind === 'video' && (
                <div className="grid grid-cols-2 gap-2" data-testid="upload-publish-as">
                  <button
                    type="button"
                    onClick={() => setPublishAs('reel')}
                    className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                      publishAs === 'reel'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:border-primary/60'
                    }`}
                    data-testid="upload-as-reel"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <Video className="w-4 h-4" />
                      <span>{tr("Reel")}</span>
                    </div>
                    <p className="text-[10px] opacity-80 mt-0.5 font-normal">Vertical 9:16</p>
                  </button>
                  <button
                    type="button"
                    onClick={() => setPublishAs('post')}
                    className={`px-3 py-2.5 rounded-xl border text-sm font-medium transition-all ${
                      publishAs === 'post'
                        ? 'bg-primary text-primary-foreground border-primary'
                        : 'bg-background text-foreground border-border hover:border-primary/60'
                    }`}
                    data-testid="upload-as-post"
                  >
                    <div className="flex items-center justify-center gap-1.5">
                      <ImageIcon className="w-4 h-4" />
                      <span>{tr("Post")}</span>
                    </div>
                    <p className="text-[10px] opacity-80 mt-0.5 font-normal">{tr("Thumb 1:1 en feed")}</p>
                  </button>
                </div>
              )}

              <Textarea
                placeholder={tr("Escribe una descripción…")}
                value={caption}
                onChange={(e) => setCaption(e.target.value)}
                maxLength={2000}
                className="bg-background border-border min-h-[140px] resize-none"
                data-testid="upload-caption"
              />
              <div>
                <p className="text-xs font-medium text-muted-foreground mb-2">{tr("Tags rápidas")}</p>
                <div className="flex flex-wrap gap-2">
                  {QUICK_TAGS.map((t) => (
                    <button
                      key={t}
                      onClick={() => toggleTag(t)}
                      className={`text-xs px-3 py-1 rounded-full border transition-colors ${
                        tags.includes(t)
                          ? 'bg-primary text-primary-foreground border-primary'
                          : 'bg-background text-muted-foreground border-border hover:border-primary/60'
                      }`}
                      data-testid={`upload-tag-${t}`}
                    >
                      #{t}
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-lg bg-destructive/10 border border-destructive/30 text-sm text-destructive" data-testid="upload-step2-error">
                  <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                  <span>{error}</span>
                </div>
              )}
            </div>
            {/* Sticky publish footer — always visible above the keyboard / overlays.
                Sits inside the dialog so it scrolls with content on tall layouts
                but stays pinned to the bottom of the viewport on mobile thanks to
                position:sticky + safe-area padding. */}
            <div
              className="md:col-span-2 sticky bottom-0 left-0 right-0 z-10 px-4 py-3 bg-card border-t border-border flex items-center gap-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]"
              data-testid="upload-publish-footer"
            >
              <Badge variant="outline" className="text-[10px] tracking-wide uppercase">
                {kind === 'image' && tr("Foto · 1:1")}
                {kind === 'video' && publishAs === 'reel' && tr("Reel · 9:16")}
                {kind === 'video' && publishAs === 'post' && tr("Post de video · 1:1")}
              </Badge>
              <Button
                className="ml-auto h-10 px-5 rounded-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold gap-1.5"
                onClick={submit}
                data-testid="upload-publish-button"
              >
                <Send className="w-4 h-4" />{tr("Publicar")}</Button>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default UploadContentDialog;
